import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import CanvasPreview from "@/components/CanvasPreview";
import api from "@/lib/api";
import { FORMATS } from "@/lib/formats";
import { toast } from "sonner";
import { Plus, LayoutTemplate, Sparkles, Trash2, Loader2, Clock } from "lucide-react";

const BANNER = "https://images.pexels.com/photos/30869730/pexels-photo-30869730.jpeg";

export default function Dashboard() {
  const navigate = useNavigate();
  const [designs, setDesigns] = useState(null);
  const [creating, setCreating] = useState(false);
  const [formatId, setFormatId] = useState("ig-portrait");

  const load = () => api.get("/designs").then((r) => setDesigns(r.data)).catch(() => setDesigns([]));
  useEffect(() => { load(); }, []);

  const createBlank = async () => {
    setCreating(true);
    try {
      const fmt = FORMATS.find((f) => f.id === formatId) || FORMATS[1];
      const { data } = await api.post("/designs", { name: fmt.label, width: fmt.w, height: fmt.h, background: "#FFFFFF", elements: [] });
      navigate(`/editor/${data.id}`);
    } catch (e) { toast.error("Could not create design"); setCreating(false); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/designs/${id}`);
    toast.success("Design deleted");
    load();
  };

  return (
    <div className="flex bg-kann-base min-h-screen">
      <Sidebar />
      <main className="flex-1 min-w-0 relative pt-14 md:pt-0">
        <div className="absolute inset-0 -z-10 bg-dots overflow-hidden"><div className="glow-orb" style={{ width: 560, height: 560, top: 40, left: "45%" }} /></div>
        <div className="relative h-40 sm:h-52 md:h-56 overflow-hidden border-b border-kann-border/20">
          <img src={BANNER} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-kann-base via-kann-base/70 to-kann-base/20" />
          <div className="relative z-10 h-full flex flex-col justify-center px-6 md:px-10">
            <div className="text-xs tracking-[0.3em] uppercase text-kann-gold font-mono">KANN Studio</div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-kann-text-pri mt-2">Create without limits</h1>
            <p className="font-light text-sm md:text-base text-kann-text-sec mt-2 max-w-lg">Design flyers, posts and artwork that stay true to your brand.</p>
          </div>
        </div>

        <div className="p-4 sm:p-6 md:p-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-kann-ink text-white rounded-lg p-6 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3"><Plus className="w-6 h-6 text-kann-gold" /><span className="font-semibold text-lg">New design</span></div>
                <p className="text-sm mt-2 text-white/70">Pick a format and start creating.</p>
              </div>
              <div className="mt-4 flex gap-2">
                <select data-testid="format-select" value={formatId} onChange={(e) => setFormatId(e.target.value)}
                  className="flex-1 min-w-0 bg-white/10 border border-white/20 rounded-md text-white text-sm px-2 py-2 outline-none focus:border-kann-gold">
                  {FORMATS.map((f) => <option key={f.id} value={f.id} className="text-black">{f.label} · {f.w}×{f.h}</option>)}
                </select>
                <button onClick={createBlank} disabled={creating} data-testid="create-blank-btn"
                  className="shrink-0 bg-kann-gold text-kann-ink font-semibold rounded-full px-5 py-2 text-sm hover:bg-yellow-500 transition-colors flex items-center gap-2 active:scale-[0.98]">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
                </button>
              </div>
            </div>
            <button onClick={() => navigate("/templates")} data-testid="goto-templates-btn"
              className="bg-kann-surface border border-kann-border/30 rounded-lg p-6 text-left hover:bg-kann-surface-active transition-colors active:scale-[0.98]">
              <div className="flex items-center gap-3 text-kann-text-pri">
                <LayoutTemplate className="w-6 h-6 text-kann-gold" /><span className="font-semibold text-lg">Browse templates</span>
              </div>
              <p className="text-sm mt-2 text-kann-text-sec">Ready-made, on-brand layouts.</p>
            </button>
            <button onClick={() => navigate("/marketing")} data-testid="goto-marketing-btn"
              className="bg-kann-surface border border-kann-border/30 rounded-lg p-6 text-left hover:bg-kann-surface-active transition-colors active:scale-[0.98]">
              <div className="flex items-center gap-3 text-kann-text-pri">
                <Sparkles className="w-6 h-6 text-kann-gold" /><span className="font-semibold text-lg">AI marketing</span>
              </div>
              <p className="text-sm mt-2 text-kann-text-sec">Generate copy, taglines & ideas.</p>
            </button>
          </div>

          <div className="mt-12">
            <div className="flex items-center gap-2 mb-5">
              <Clock className="w-4 h-4 text-kann-gold" />
              <h2 className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono">Your designs</h2>
            </div>

            {designs === null ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-kann-gold animate-spin" /></div>
            ) : designs.length === 0 ? (
              <div className="border border-dashed border-kann-border/40 rounded-lg py-20 text-center">
                <p className="text-kann-text-sec">No designs yet. Create your first one above.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="designs-grid">
                {designs.map((d) => (
                  <div key={d.id} onClick={() => navigate(`/editor/${d.id}`)} data-testid={`design-card-${d.id}`}
                    className="group cursor-pointer bg-kann-surface border border-kann-border/30 rounded-lg overflow-hidden hover:border-kann-gold/60 transition-colors">
                    <div className="w-full aspect-[4/5] bg-kann-base flex items-center justify-center overflow-hidden">
                      <CanvasPreview design={d} targetWidth={220} />
                    </div>
                    <div className="p-3 flex items-center justify-between gap-2">
                      <span className="text-sm text-kann-text-pri truncate">{d.name}</span>
                      <button onClick={(e) => remove(d.id, e)} data-testid={`delete-design-${d.id}`}
                        className="text-kann-text-sec hover:text-red-400 transition-colors opacity-100 md:opacity-0 md:group-hover:opacity-100">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
